/* eslint-disable no-restricted-globals, no-unused-vars */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — card DOM templates
// Each card is a real HTML element placed in 3D and projected each frame. It
// holds a collapsed summary AND a full body that's revealed in-place on zoom.
// ─────────────────────────────────────────────────────────────────────────
import { PROJECTS, FINANCE, INVEST, BUDGET, TRAVEL, ZONES, EXTRA_ZONES,
  VITALS, CAPTURE, SYSTEM, TODAY } from './data.js';
import { gIcon, ZONE_GLYPH, PROJECT_GLYPH } from './glyphs.js';
import { getView, setView } from './viewState.js';
import { VitalsService } from '../services/vitalsService';
import { CaptureService } from '../services/captureService';

export const fmtDue = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
const esc = s => (s == null ? '' : String(s));

// ── descriptors: flat model the world builds cards + nav from ───────────────
export function buildModel() {
  const zones = ZONES.map(z => {
    let items = [];
    if (z.id === 'projects') items = PROJECTS.map(p => ({ id: 'p-' + p.id, kind: 'project', data: p }));
    // finance descriptors stay MINIMAL on purpose: financeCard() reads the live
    // FINANCE object so edits reflect without rebuilding the descriptor.
    if (z.id === 'finances') items = [
      { id: 'fin-runway', kind: 'finance', data: { view: 'runway', title: 'Runway', code: 'FIN-RW' } },
      { id: 'fin-invest', kind: 'finance', data: { view: 'invest', title: 'Investments', code: 'FIN-IV' } },
      { id: 'fin-budget', kind: 'finance', data: { view: 'budget', title: 'Budget', code: 'FIN-BG' } },
    ];
    if (z.id === 'travel') items = TRAVEL.map(t => ({ id: 'tr-' + t.id, kind: 'travel', data: t }));
    // VITALS / CAPTURE descriptors stay minimal — the renderers read the live
    // VITALS / CAPTURE objects so edits reflect without rebuilding descriptors.
    if (z.id === 'vitals') items = [
      { id: 'vi-mind', kind: 'mind', data: { code: 'VI-MN' } },
      { id: 'vi-body', kind: 'body', data: { code: 'VI-BD' } },
    ];
    if (z.id === 'capture') items = [
      { id: 'cap-inbox', kind: 'capture', data: { code: 'CAP-IN' } },
    ];
    if (z.id === 'system') items = SYSTEM.nodes.map(n => ({ id: 'sys-' + n.id, kind: 'node', data: n }));
    return { id: 'z-' + z.id, kind: 'zone', data: z, items };
  });
  // runtime-spawned zones (mock CMS) — their nodes render as project-style cards
  (EXTRA_ZONES || []).forEach(z => {
    zones.push({
      id: 'z-' + z.id, kind: 'zone', data: z,
      items: (z.nodes || []).map(n => ({ id: 'p-' + n.id, kind: 'project', data: n })),
    });
  });
  return { zones };
}

// ── inner HTML for a descriptor (also used for live re-render on edit) ───────
export function cardInner(desc) {
  return desc.kind === 'zone' ? zoneCard(desc.data)
       : desc.kind === 'project' ? projectCard(desc.data)
       : desc.kind === 'finance' ? financeCard(desc.data)
       : desc.kind === 'mind' ? mindCard(desc.data)
       : desc.kind === 'body' ? bodyCard(desc.data)
       : desc.kind === 'capture' ? captureCard(desc.data)
       : desc.kind === 'node' ? nodeCard(desc.data)
       : travelCard(desc.data);
}

// ── build a card element for a descriptor ───────────────────────────────────
export function buildCard(desc) {
  const el = document.createElement('div');
  el.className = 'card3d ' + (desc.kind === 'zone' ? 'zone' : 'item');
  el.dataset.card = desc.id;
  el.innerHTML = cardInner(desc);
  return el;
}

// ── ZONE ────────────────────────────────────────────────────────────────────
function zoneCard(z) {
  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-top"><span class="c-code">${z.code}</span><span class="c-live"><i></i>LIVE</span></div>
    <div class="c-zbody">
      <div class="c-glyph">${gIcon(ZONE_GLYPH[z.id] || 'proj-a', 34)}</div>
      <div class="c-title">${z.label}</div>
      <div class="c-sub">${z.sub.toUpperCase()}</div>
    </div>
    <div class="c-foot"><span>${(z.children || z.nodes || []).length} NODES</span><span class="c-enter">ENTER ▸</span></div>`;
}

// ── PROJECT ──────────────────────────────────────────────────────────────────
function projectCard(p) {
  const done = p.tasks.filter(t => t.s === 'Done').length;
  const pillC = p.priority === 'High' ? 'hi' : 'med';
  const due = p.due ? `DUE ${fmtDue(p.due)}` : (p.tag || '');
  const gid = p.glyph || PROJECT_GLYPH[p.id] || 'cit';
  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon(gid, 14, 'cmark')}<span class="c-code">${p.tag}</span></span><span class="c-pill ${pillC}">${p.priority.toUpperCase()}</span></div>
      <div class="c-title sm">${title(p.title)}</div>
      <div class="c-teaser">${p.next}</div>
      <div class="c-foot"><span class="c-status ok">● ${p.status.toUpperCase()}</span><span>${due}</span></div>
    </div>
    <div class="c-full">
      ${fullHead(p.tag, p.title, [tag(p.status.toUpperCase(),'ok'), tag(p.priority.toUpperCase(),pillC), p.due?tag('DUE '+fmtDue(p.due)+' '+new Date(p.due).getFullYear(),''):''], gid)}
      <div class="c-scroll">
        <div class="c-col">
          ${sect('Next Action', `<div class="nextbox">${p.next}</div>`)}
          ${sect('Brief', `<div class="desc">${p.desc}</div>`)}
        </div>
        <div class="c-col">
          ${sect(`Tasks — ${done}/${p.tasks.length} done`, taskList(p.tasks))}
          ${sect('Artifacts', artGrid(p.artifacts))}
        </div>
      </div>
    </div>`;
}

// ── FINANCE ──────────────────────────────────────────────────────────────────
function financeCard(desc) {
  if (desc.view === 'invest') return investCard(desc);
  if (desc.view === 'budget') return budgetCard(desc);
  const f = { ...FINANCE, ...desc };   // live FINANCE + this view's title/code
  const lead = f.headline;
  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon('fin', 14, 'cmark')}<span class="c-code">${f.code}</span></span><span class="c-pill med">AUDITING</span></div>
      <div class="c-title sm">${f.title}</div>
      <div class="c-teaser">${lead}. ${f.note}</div>
      <div class="c-foot"><span class="c-status ok">● ACTIVE</span><span>FINANCES</span></div>
    </div>
    <div class="c-full">
      ${fullHead(f.code, f.title, [tag('AUDITING','med')], 'fin')}
      <div class="c-scroll">
        <div class="c-col">
          ${sect(f.headline, `<div class="desc">${f.note}</div>`)}
          ${sect('Snapshot', metricGrid(f.metrics))}
        </div>
        <div class="c-col">
          ${sect('Threads', threadList(f.threads))}
        </div>
      </div>
    </div>`;
}

// ── INVESTMENTS ───────────────────────────────────────────────────────────────
// The selected period is VIEW state (persisted, decoupled from the INVEST data
// object) so a live re-price — which swaps the whole INVEST object via
// setLiveInvestments — never resets the user's window. The render reads
// currentInvestPeriod(); a refresh re-renders against the unchanged selection.
export function currentInvestPeriod() {
  const stored = getView('invest.period', null);
  if (stored && INVEST.periods.includes(stored)) return stored;
  // fall back to the data's own default window (or the first valid period)
  return INVEST.periods.includes(INVEST.period) ? INVEST.period : INVEST.periods[0];
}
export function setInvestPeriod(p) { if (INVEST.periods.includes(p)) setView('invest.period', p); }

const ivMoney = (n, dp = 0) => INVEST.currency + Math.abs(n).toLocaleString('en-US',
  { minimumFractionDigits: dp, maximumFractionDigits: dp });
function ivTone(x) { return x > 0 ? 'up' : x < 0 ? 'down' : 'flat'; }
function ivArrow(x) { return x > 0 ? '▲' : x < 0 ? '▼' : '—'; }
function ivPct(x) { const s = x > 0 ? '+' : x < 0 ? '−' : ''; return s + Math.abs(x).toFixed(2) + '%'; }

function investCard(desc) {
  const iv = INVEST;
  const per = currentInvestPeriod();
  const d = iv.delta[per] || { abs: 0, pct: 0 };
  const tone = ivTone(d.pct);
  const sumVal = iv.holdings.reduce((a, h) => a + h.value, 0) || iv.total;

  // collapsed teaser shows live value + the selected window's move
  const teaser = `${ivMoney(iv.total)} &middot; <span class="tone ${tone}">${ivArrow(d.pct)} ${ivPct(d.pct)}</span> over ${per}`;

  // segmented period control
  const seg = `<div class="iv-seg" data-iv-seg>${iv.periods.map(p =>
    `<button class="iv-seg-b${p === per ? ' on' : ''}" data-period="${p}">${p}</button>`).join('')}</div>`;

  // holdings list — value + change for the selected window
  const rows = iv.holdings.map(h => {
    const ch = (h.d && h.d[per] != null) ? h.d[per] : 0;
    const t = ivTone(ch);
    const w = Math.max(2, Math.round((h.value / sumVal) * 100));
    const qty = h.qty == null ? '' : (h.sym === 'BTC' ? `${h.qty} BTC` : `${h.qty} sh`);
    return `<div class="iv-row">
      <div class="iv-tk">
        <span class="iv-sym">${h.sym}</span>
        <span class="iv-nm">${h.name}${qty ? `<span class="iv-qty"> &middot; ${qty}</span>` : ''}</span>
        <span class="iv-bar"><i style="width:${w}%"></i></span>
      </div>
      <div class="iv-num">
        <span class="iv-val">${ivMoney(h.value)}</span>
        <span class="iv-chg tone ${t}">${ivArrow(ch)} ${ivPct(ch)}</span>
      </div>
    </div>`;
  }).join('');

  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon('fin', 14, 'cmark')}<span class="c-code">${desc.code}</span></span><span class="c-pill ${tone === 'down' ? 'hi' : 'med'}">${tone === 'down' ? 'DOWN' : tone === 'up' ? 'UP' : 'FLAT'}</span></div>
      <div class="c-title sm">${desc.title}</div>
      <div class="c-teaser iv-teaser">${teaser}</div>
      <div class="c-foot"><span class="c-status ok">● LIVE</span><span>FINANCES</span></div>
    </div>
    <div class="c-full">
      ${fullHead(desc.code, 'Investments', [tag('PORTFOLIO','ok'), tag(tone === 'down' ? 'DOWN ' + per : 'UP ' + per, tone === 'down' ? 'hi' : 'med')], 'fin')}
      <div class="c-scroll iv-scroll">
        <div class="c-col iv-left">
          <div class="iv-hero">
            <div class="iv-cap">TOTAL PORTFOLIO VALUE</div>
            <div class="iv-total">${ivMoney(iv.total)}</div>
            <div class="iv-delta tone ${tone}">
              <span class="iv-arw">${ivArrow(d.pct)}</span>
              <span class="iv-abs">${d.abs > 0 ? '+' : d.abs < 0 ? '−' : ''}${ivMoney(d.abs)}</span>
              <span class="iv-pct">${ivPct(d.pct)}</span>
              <span class="iv-win">over ${per}</span>
            </div>
            ${seg}
            <div class="iv-asof">AS OF ${iv.asof.toUpperCase()}</div>
          </div>
        </div>
        <div class="c-col iv-right">
          <div class="sect">
            <h2>Holdings &mdash; ${iv.holdings.length} positions</h2>
            <div class="iv-list">
              <div class="iv-head"><span>POSITION</span><span>VALUE / ${per}</span></div>
              ${rows}
            </div>
          </div>
        </div>
      </div>
    </div>`;
}

// ── BUDGET ────────────────────────────────────────────────────────────────────
// Live-injected via setLiveBudget(); read-only (bank-CSV import via Claude Code).
// Reuses the iv-* (investments) styling for a consistent finance look.
function budgetCard(desc) {
  const b = BUDGET;
  const cur = b.currency || 'CZK';
  const money = n => Math.round(Number(n) || 0).toLocaleString('en-US') + ' ' + cur;
  const t = b.totals || { spent: 0, income: 0, net: 0 };
  const netTone = t.net > 0 ? 'up' : t.net < 0 ? 'down' : 'flat';
  const netStr = (t.net >= 0 ? '+' : '−') + money(Math.abs(t.net));

  const cats = (b.categories || []).filter(c => c.spent > 0 || c.budget > 0)
    .slice().sort((a, c) => c.spent - a.spent);
  const maxSpent = cats.reduce((m, c) => Math.max(m, c.spent), 0) || 1;

  const teaser = `${b.month} &middot; <span class="tone down">${money(t.spent)}</span> spent &middot; net <span class="tone ${netTone}">${netStr}</span>`;

  const rows = cats.map(c => {
    const over = c.budget > 0 && c.spent > c.budget;
    const w = Math.max(2, Math.round((c.spent / maxSpent) * 100));
    const note = c.budget > 0
      ? `<span class="iv-chg tone ${over ? 'down' : 'up'}">/ ${money(c.budget)}</span>`
      : '';
    return `<div class="iv-row">
      <div class="iv-tk">
        <span class="iv-sym">${esc(c.label)}</span>
        <span class="iv-nm">${esc(c.group || '')}</span>
        <span class="iv-bar"><i style="width:${w}%"></i></span>
      </div>
      <div class="iv-num">
        <span class="iv-val">${money(c.spent)}</span>
        ${note}
      </div>
    </div>`;
  }).join('');

  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon('fin', 14, 'cmark')}<span class="c-code">${desc.code}</span></span><span class="c-pill ${netTone === 'down' ? 'hi' : 'med'}">${netTone === 'up' ? 'SURPLUS' : netTone === 'down' ? 'DEFICIT' : 'FLAT'}</span></div>
      <div class="c-title sm">${desc.title}</div>
      <div class="c-teaser iv-teaser">${teaser}</div>
      <div class="c-foot"><span class="c-status ok">● LIVE</span><span>FINANCES</span></div>
    </div>
    <div class="c-full">
      ${fullHead(desc.code, 'Budget', [tag('MONTH ' + b.month, 'ok'), tag(netTone === 'down' ? 'DEFICIT' : 'SURPLUS', netTone === 'down' ? 'hi' : 'med')], 'fin')}
      <div class="c-scroll iv-scroll">
        <div class="c-col iv-left">
          <div class="iv-hero">
            <div class="iv-cap">SPENT &middot; ${b.month}</div>
            <div class="iv-total">${money(t.spent)}</div>
            <div class="iv-delta tone ${netTone}">
              <span class="iv-abs">NET ${netStr}</span>
              <span class="iv-win">IN ${money(t.income)}</span>
            </div>
            <div class="iv-asof">${(b.transactions || []).length} TRANSACTIONS</div>
          </div>
        </div>
        <div class="c-col iv-right">
          <div class="sect">
            <h2>By category &mdash; ${cats.length}</h2>
            <div class="iv-list">
              <div class="iv-head"><span>CATEGORY</span><span>SPENT / BUDGET</span></div>
              ${rows}
            </div>
          </div>
        </div>
      </div>
    </div>`;
}

// ── TRAVEL ───────────────────────────────────────────────────────────────────
const TRV_MON = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const TRV_LIFECYCLE = ['idea','planned','booked','active','done'];
const TRV_STATUS = {
  idea:    { label: 'IDEA',    cls: 'idea' },
  planned: { label: 'PLANNED', cls: 'planned' },
  booked:  { label: 'BOOKED',  cls: 'booked' },
  active:  { label: 'ACTIVE',  cls: 'active' },
  done:    { label: 'DONE',    cls: 'done' },
};
function trvStatus(s) { return TRV_STATUS[String(s || '').toLowerCase()] || { label: String(s || '—').toUpperCase(), cls: 'idea' }; }
function trvDot(s) { return `<span class="sdot ${trvStatus(s).cls}"></span>`; }
function trvStTag(s) { const st = trvStatus(s); return `<span class="trv-st">${trvDot(s)}${st.label}</span>`; }
function trvParts(iso) { if (!iso) return null; const d = new Date(iso); return { d: d.getUTCDate(), m: d.getUTCMonth(), y: d.getUTCFullYear() }; }
function trvDay(iso) { const p = trvParts(iso); return p ? String(p.d).padStart(2, '0') + ' ' + TRV_MON[p.m] : '—'; }
function trvRange(s, e) {
  const a = trvParts(s), b = trvParts(e);
  if (!a && !b) return 'Ongoing';
  if (a && !b) return trvDay(s) + ' →';
  if (!a && b) return '→ ' + trvDay(e);
  if (a.m === b.m && a.y === b.y) return String(a.d).padStart(2, '0') + '–' + String(b.d).padStart(2, '0') + ' ' + TRV_MON[a.m] + ' ' + a.y;
  return trvDay(s) + ' – ' + trvDay(e) + ' ' + b.y;
}
function trvNights(s, e) { if (!s || !e) return null; const n = Math.round((new Date(e) - new Date(s)) / 864e5); return n > 0 ? n : null; }
function trvWindowTag(t) {
  const r = trvRange(t.dates && t.dates.start, t.dates && t.dates.end);
  const n = trvNights(t.dates && t.dates.start, t.dates && t.dates.end);
  return r + (n ? ` · ${n}N` : '') + (t.dates && t.dates.flexible ? ' · FLEX' : '');
}
function trvLink(pid) {
  if (!pid) return '';
  const p = PROJECTS.find(x => x.id === pid);
  if (!p) return '';
  return `<button class="trv-link" data-goto="p-${p.id}">${gIcon(p.glyph || PROJECT_GLYPH[p.id] || 'cit', 13, 'cmark')}<span class="trv-link-tag">${p.tag}</span><span class="trv-link-t">${title(p.title)}</span><span class="trv-link-go">▸</span></button>`;
}
function trvTransport(legs) {
  if (!legs || !legs.length) return `<div class="trv-empty">No transport booked yet</div>`;
  return `<div class="leglist">${legs.map(l => `
    <div class="leg">
      <span class="leg-mode">${esc(l.mode).toUpperCase()}</span>
      <span class="leg-route"><b>${esc(l.from) || '—'}</b><i class="leg-arr">→</i><b>${esc(l.to) || '—'}</b></span>
      <span class="leg-meta">${trvDay(l.date)}${l.ref ? ` · ${esc(l.ref)}` : ''}</span>
      ${trvStTag(l.status)}
    </div>`).join('')}</div>`;
}
function trvStay(stays) {
  if (!stays || !stays.length) return `<div class="trv-empty">No accommodation yet</div>`;
  return `<div class="staylist">${stays.map(s => {
    const n = trvNights(s.checkIn, s.checkOut);
    const win = (s.checkIn || s.checkOut) ? `${trvDay(s.checkIn)} – ${trvDay(s.checkOut)}${n ? ` · ${n}N` : ''}` : 'Open-ended';
    return `<div class="stay">
      <div class="stay-l">
        <span class="stay-name">${esc(s.name) || 'TBD'}</span>
        <span class="stay-city">${esc(s.city) || ''}</span>
      </div>
      <div class="stay-r">
        <span class="stay-win">${win}${s.ref ? ` · ${esc(s.ref)}` : ''}</span>
        ${trvStTag(s.status)}
      </div>
    </div>`;
  }).join('')}</div>`;
}
function trvCheck(items) {
  if (!items || !items.length) return `<div class="trv-empty">No checklist items</div>`;
  return `<div class="tasklist">${items.map(i => `
    <div class="trow ${i.done ? 'done' : ''}"><span class="tstate"></span><span class="ttxt">${esc(i.label)}</span><span class="tstatus">${i.done ? 'DONE' : 'TO DO'}</span></div>`).join('')}</div>`;
}

function travelCard(t) {
  const st = trvStatus(t.status);
  const dateTeaser = trvRange(t.dates && t.dates.start, t.dates && t.dates.end);
  const checks = t.checklist || [];
  const done = checks.filter(c => c.done).length;
  const stepIdx = TRV_LIFECYCLE.indexOf(String(t.status || '').toLowerCase());
  const rail = TRV_LIFECYCLE.map((s, i) =>
    `<span class="lc-step${i <= stepIdx && stepIdx >= 0 ? ' on' : ''}${i === stepIdx ? ' now' : ''}">${TRV_STATUS[s].label}</span>`).join('<i class="lc-sep"></i>');
  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon('trv-b', 14, 'cmark')}<span class="c-code">${esc(t.destination)}</span></span><span class="c-pill ${st.cls === 'active' ? 'ok' : st.cls === 'idea' || st.cls === 'done' ? '' : 'med'}">${st.label}</span></div>
      <div class="c-title sm">${esc(t.title)}</div>
      <div class="c-teaser">${esc(t.purpose)}</div>
      <div class="c-foot"><span class="c-status ok">● ${dateTeaser.toUpperCase()}</span><span>${done}/${checks.length} ✓</span></div>
    </div>
    <div class="c-full">
      ${fullHead(esc(t.destination), esc(t.title), [tag(st.label, st.cls === 'idea' ? '' : st.cls === 'done' ? 'ok' : 'med'), tag(trvWindowTag(t), '')], 'trv-b')}
      <div class="trv-rail">${rail}</div>
      <div class="c-scroll">
        <div class="c-col">
          ${sect('Purpose', `<div class="nextbox">${esc(t.purpose)}</div>${t.linkedProject ? `<div class="trv-linkwrap">${trvLink(t.linkedProject)}</div>` : ''}`)}
          ${sect('Transport', trvTransport(t.transport))}
          ${sect('Accommodation', trvStay(t.accommodation))}
        </div>
        <div class="c-col">
          ${sect(`Checklist — ${done}/${checks.length} done`, trvCheck(checks))}
          ${sect('Notes', t.notes ? `<div class="desc">${esc(t.notes)}</div>` : `<div class="trv-empty">No notes yet</div>`)}
        </div>
      </div>
    </div>`;
}

// ── shared bits ───────────────────────────────────────────────────────────────
function title(s) { return esc(s).replace(' — ', ' — ').replace(' – ', ' – '); }
function fullHead(code, t, tags, gid) {
  const codeLine = gid
    ? `<div class="c-code has-mark">${gIcon(gid, 16, 'cmark')}<span>${code}</span></div>`
    : `<div class="c-code">${code}</div>`;
  return `<button class="c-close" aria-label="close">✕</button>
    <div class="c-fhead">${codeLine}<div class="c-ftitle">${t}</div>
    <div class="c-tags">${tags.filter(Boolean).join('')}</div></div>`;
}
function tag(l, c) { return `<span class="tag ${c}">${l}</span>`; }
function sect(h, body) { return `<div class="sect"><h2>${h}</h2>${body}</div>`; }
function taskList(tasks) {
  return `<div class="tasklist">${tasks.map(t => {
    const cls = t.s === 'Done' ? 'done' : t.s === 'In Progress' ? 'prog' : '';
    return `<div class="trow ${cls}"><span class="tstate"></span><span class="ttxt">${t.t}</span><span class="tstatus">${t.s.toUpperCase()}</span></div>`;
  }).join('')}</div>`;
}
function threadList(threads) {
  return `<div class="tasklist">${threads.map(t => {
    const cls = t.s === 'In Progress' ? 'prog' : t.s === 'Done' ? 'done' : '';
    return `<div class="trow ${cls}"><span class="tstate"></span><span class="ttxt">${t.t}<span class="tnote"> — ${t.note}</span></span><span class="tstatus">${t.s.toUpperCase()}</span></div>`;
  }).join('')}</div>`;
}
function artGrid(arts) {
  return `<div class="artgrid">${(arts || []).map(a => {
    const label = typeof a === 'string' ? a : (a.title || '');
    const wide = label.length > 26 ? ' wide' : '';
    return `<div class="artchip${wide}"><span class="aico"></span><span>${esc(label)}</span></div>`;
  }).join('')}</div>`;
}
function metricGrid(metrics) {
  return `<div class="metricgrid">${(metrics || []).map(m =>
    `<div class="metric"><div class="mval">${m.value}</div><div class="mlabel">${m.label}</div><div class="mhint">${m.hint}</div></div>`).join('')}</div>`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// VITALS · CAPTURE · SYSTEM — three zones sharing one meter vocabulary. The
// shared bar() gauge reads a mood, a night's sleep, and a Pi's CPU temp alike
// (System visually mirrors Body). Mutations live beside the renderers and
// persist() so a refresh keeps the operator's Mind + Capture state (until PR C
// swaps the localStorage draft for the S3 + API backend).
// ═══════════════════════════════════════════════════════════════════════════════
const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const clamp01 = x => Math.max(0, Math.min(1, x));
const round = (n, dp = 0) => { const f = 10 ** dp; return Math.round(n * f) / f; };
function dayDiff(iso) {
  const d = new Date(iso.slice(0, 10) + 'T00:00:00');
  return Math.round((TODAY - d) / 864e5);
}
function fmtDay(iso) { const d = new Date(iso.slice(0, 10) + 'T00:00:00'); return String(d.getUTCDate()).padStart(2, '0') + ' ' + MON[d.getUTCMonth()]; }
function capAge(at) {
  const diff = dayDiff(at);
  if (diff <= 0) { const tm = at.split('T')[1]; return tm ? tm.slice(0, 5) : 'TODAY'; }
  if (diff === 1) return 'YESTERDAY';
  return diff + 'D AGO';
}

// ── the shared gauge — a hairline meter with optional target tick + tone ───────
// tone: 'cool' | 'ok' | 'warn' | 'crit' | '' (accent). spark = array → sparkline.
function bar({ label, val, pct, tone = '', target = null, hint = '', spark = null, invert = false }) {
  const w = (clamp01(pct) * 100).toFixed(1);
  const tk = target != null ? `<span class="vg-tick" style="left:${(clamp01(target) * 100).toFixed(1)}%"></span>` : '';
  return `<div class="vg">
    <div class="vg-top"><span class="vg-label">${label}</span><span class="vg-val">${val}</span></div>
    <div class="vg-meter"><i class="vg-fill ${tone}" style="width:${w}%"></i>${tk}</div>
    ${(hint || spark) ? `<div class="vg-foot"><span class="vg-hint">${hint}</span>${spark ? sparkline(spark, invert) : ''}</div>` : ''}
  </div>`;
}
function sparkline(arr, invert = false) {
  if (!arr || arr.length < 2) return '';
  const min = Math.min(...arr), max = Math.max(...arr), span = (max - min) || 1;
  const W = 64, H = 16;
  const pts = arr.map((v, i) => {
    const x = (i / (arr.length - 1)) * W;
    const norm = (v - min) / span;            // 0..1 low..high
    const y = H - norm * (H - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const last = arr[arr.length - 1], lx = W, ly = H - ((last - min) / span) * (H - 2) - 1;
  return `<svg class="vg-spark" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">
    <polyline points="${pts}"/><circle cx="${lx - 1.4}" cy="${ly.toFixed(1)}" r="1.6"/></svg>`;
}

// ── VITALS · Mind ──────────────────────────────────────────────────────────────
function moodTone(v) { return v <= 2 ? 'm-low' : v === 3 ? 'm-mid' : 'm-hi'; }
function moodBar(v, interactive = false, big = false) {
  const tone = v ? moodTone(v) : '';
  let s = `<div class="mbar${big ? ' big' : ''} ${tone}">`;
  for (let i = 1; i <= 5; i++) {
    const on = v && i <= v ? ' on' : '';
    s += interactive
      ? `<button class="mseg${on}" data-mood="${i}" aria-label="level ${i}"></button>`
      : `<span class="mseg${on}"></span>`;
  }
  return s + `</div>`;
}
function mindCard(d) {
  const m = VITALS.mind;
  const logged = m.today != null;
  const label = logged ? m.labels[m.today] : 'NOT LOGGED';
  // history = today's live entry (if logged) prepended to the dated log
  const hist = (logged ? [{ d: TODAY.toISOString(), v: m.today, n: m.note, today: true }] : []).concat(m.log);
  const avg = m.log.length ? round(m.log.slice(0, 7).reduce((a, e) => a + e.v, 0) / Math.min(7, m.log.length), 1) : 0;
  const histRows = hist.map(e => `
    <div class="mh-row${e.today ? ' now' : ''}">
      <span class="mh-date">${e.today ? 'TODAY' : fmtDay(e.d)}</span>
      ${moodBar(e.v)}
      <span class="mh-note">${e.n ? esc(e.n) : '<i>—</i>'}</span>
    </div>`).join('');

  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon('v-mind', 14, 'cmark')}<span class="c-code">${d.code}</span></span><span class="c-pill ${logged ? 'ok' : 'med'}">${logged ? label : 'LOG'}</span></div>
      <div class="c-title sm">Mind</div>
      ${moodBar(m.today)}
      <div class="c-teaser">${logged ? (m.note ? esc(m.note) : 'Logged for today · ' + label.toLowerCase()) : 'Daily check-in — tap to log how today feels.'}</div>
      <div class="c-foot"><span class="c-status ok">● MENTAL</span><span>7-DAY ${avg}</span></div>
    </div>
    <div class="c-full">
      ${fullHead(d.code, 'Mind', [tag('CHECK-IN', 'ok'), tag('7-DAY AVG ' + avg, 'med')], 'v-mind')}
      <div class="c-scroll">
        <div class="c-col">
          ${sect('Today', `
            <div class="mind-today">
              <div class="mt-bar">${moodBar(m.today, true, true)}</div>
              <div class="mt-scale"><span>1 · depleted</span><span>5 · peak</span></div>
              <div class="mt-state ${logged ? moodTone(m.today) : 'off'}">${logged ? m.today + ' / 5 · ' + label : 'TAP A LEVEL TO LOG TODAY'}</div>
              <div class="mind-journal ${logged ? '' : 'disabled'}">
                <input class="mj-input" data-journal maxlength="120" value="${esc(m.note)}" placeholder="${logged ? 'Attach a line to today (optional)…' : 'Log a level first, then add a line'}" ${logged ? '' : 'disabled'} />
                <span class="mj-hint">↵</span>
              </div>
            </div>`)}
        </div>
        <div class="c-col">
          ${sect('History', `<div class="mh-list">${histRows}</div>`)}
        </div>
      </div>
    </div>`;
}

// ── VITALS · Body (mocked gauges; phone-side Apple Health exporter writes them) ─
function bodyCard(d) {
  const b = VITALS.body;
  const gline = g => {
    const pct = (g.value - g.min) / (g.max - g.min);
    const tpct = (g.target - g.min) / (g.max - g.min);
    let tone;
    if (g.invert) tone = g.value <= g.target ? 'ok' : g.value <= g.target + (g.max - g.min) * 0.18 ? 'warn' : 'crit';
    else tone = g.value >= g.target ? 'ok' : g.value >= g.target * 0.7 ? 'warn' : 'cool';
    const valTxt = (g.id === 'steps' ? g.value.toLocaleString('en-US') : g.value) + (g.unit ? ' ' + g.unit : '');
    return bar({ label: g.label, val: valTxt, pct, tone, target: tpct, hint: g.hint, spark: g.trend, invert: g.invert });
  };
  const mini = b.gauges.map(g => {
    const pct = (g.value - g.min) / (g.max - g.min);
    const valTxt = (g.id === 'steps' ? (g.value / 1000).toFixed(1) + 'k' : g.value) + (g.unit && g.unit !== 'bpm' ? g.unit : '');
    return `<div class="bm"><span class="bm-ico">${gIcon(g.glyph, 13)}</span><span class="bm-bar"><i style="width:${(clamp01(pct) * 100).toFixed(0)}%"></i></span><span class="bm-val">${valTxt}</span></div>`;
  }).join('');
  const week = Array.from({ length: 7 }, (_, i) => `<span class="wk-dot${i < b.workoutsWk ? ' on' : ''}"></span>`).join('');

  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon('v-body', 14, 'cmark')}<span class="c-code">${d.code}</span></span><span class="c-pill med">AMBIENT</span></div>
      <div class="c-title sm">Body</div>
      <div class="bm-list">${mini}</div>
      <div class="c-foot"><span class="c-status ok">● PHYSICAL</span><span>${b.workoutsWk} WORKOUTS / WK</span></div>
    </div>
    <div class="c-full">
      ${fullHead(d.code, 'Body', [tag('AMBIENT', 'med'), tag('SLOW-MOVING', '')], 'v-body')}
      <div class="c-scroll">
        <div class="c-col">
          ${sect('Gauges', `<div class="vg-list">${b.gauges.map(gline).join('')}</div>`)}
        </div>
        <div class="c-col">
          ${sect('This week', `
            <div class="bw">
              <div class="bw-row"><span class="bw-k">WORKOUTS</span><span class="bw-dots">${week}</span><span class="bw-v">${b.workoutsWk}/7</span></div>
              <div class="bw-note">Resting HR trending down — overnight lows steady around the mid-50s. Sleep is the lever; everything else follows it.</div>
            </div>`)}
        </div>
      </div>
    </div>`;
}

// ── CAPTURE · quick inbox ──────────────────────────────────────────────────────
function captureCard(d) {
  const items = CAPTURE.items;
  const inbox = items.filter(i => i.state === 'inbox');
  const notes = items.filter(i => i.state === 'note');
  const filter = currentCaptureFilter();
  const latest = items[0];
  const shown = items.filter(i => filter === 'all' ? true : i.state === filter);

  const projTag = pid => { const p = PROJECTS.find(x => x.id === pid); return p ? p.tag : null; };

  const rows = shown.map(it => {
    const routing = CAPTURE._routing === it.id;
    const rTag = it.route ? projTag(it.route) : null;
    const meta = it.state === 'note'
      ? `<span class="qi-badge note">NOTE</span>${rTag ? `<span class="qi-route">▸ ${rTag}</span>` : ''}<span class="qi-age">${capAge(it.at)}</span>`
      : `<span class="qi-badge in">INBOX</span><span class="qi-age">${capAge(it.at)}</span>`;
    const actions = it.state === 'inbox'
      ? `<button class="qi-act keep" data-cap-keep="${it.id}">KEEP</button>
         <button class="qi-act route${routing ? ' on' : ''}" data-cap-route="${it.id}">ROUTE ▸</button>
         <button class="qi-act discard" data-cap-discard="${it.id}">DISCARD</button>`
      : `<button class="qi-act route${routing ? ' on' : ''}" data-cap-route="${it.id}">${rTag ? 'RE-ROUTE' : 'ROUTE ▸'}</button>
         <button class="qi-act discard" data-cap-discard="${it.id}">DISCARD</button>`;
    const menu = routing ? `<div class="qi-menu">${PROJECTS.map(p =>
      `<button class="qi-proj${it.route === p.id ? ' on' : ''}" data-cap-to="${it.id}" data-proj="${p.id}">${gIcon(p.glyph || PROJECT_GLYPH[p.id] || 'cit', 12, 'cmark')}<span>${p.tag}</span></button>`).join('')}</div>` : '';
    return `<div class="qi${it.state === 'note' ? ' is-note' : ''}">
      <div class="qi-main"><div class="qi-text">${esc(it.text)}</div><div class="qi-meta">${meta}</div></div>
      <div class="qi-acts">${actions}</div>${menu}
    </div>`;
  }).join('') || `<div class="cap-empty">Inbox zero. Nothing waiting.</div>`;

  const seg = ['all', 'inbox', 'note'].map(f =>
    `<button class="cap-seg-b${filter === f ? ' on' : ''}" data-cap-filter="${f}">${f === 'note' ? 'NOTES' : f.toUpperCase()}${f === 'inbox' ? ' ' + inbox.length : f === 'note' ? ' ' + notes.length : ''}</button>`).join('');

  return `
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon('cap-a', 14, 'cmark')}<span class="c-code">${d.code}</span></span><span class="c-pill ${inbox.length ? 'med' : 'ok'}">${inbox.length} IN</span></div>
      <div class="c-title sm">Quick Capture</div>
      <div class="c-teaser">${latest ? esc(latest.text) : 'Drop a thought in two seconds.'}</div>
      <div class="c-foot"><span class="c-status ok">● INBOX</span><span>${notes.length} KEPT</span></div>
    </div>
    <div class="c-full">
      ${fullHead(d.code, 'Quick Capture', [tag(inbox.length + ' IN INBOX', inbox.length ? 'med' : 'ok'), tag(notes.length + ' KEPT', '')], 'cap-a')}
      <div class="cap-field">
        <span class="cf-plus">+</span>
        <input class="cf-input" data-capture maxlength="180" placeholder="Capture a thought — press ↵ to drop it in the inbox" />
        <span class="cf-ent">↵</span>
      </div>
      <div class="cap-filter">${seg}</div>
      <div class="c-scroll cap-scroll"><div class="cap-list">${rows}</div></div>
    </div>`;
}

// ── SYSTEM · homelab node ──────────────────────────────────────────────────────
function nodeStatus(n) {
  if (n.sinceBeat >= SYSTEM.deadAt) return { key: 'down', label: 'DOWN', cls: 'st-down' };
  if (n.sinceBeat >= SYSTEM.staleAt) return { key: 'stale', label: 'STALE', cls: 'st-stale' };
  if (n.services.some(s => !s.up)) return { key: 'degraded', label: 'DEGRADED', cls: 'st-degraded' };
  return { key: 'up', label: 'UP', cls: 'st-up' };
}
function beatTxt(n) {
  const s = Math.round(n.sinceBeat);
  if (s < 60) return s + 's ago';
  return Math.floor(s / 60) + 'm ' + (s % 60) + 's ago';
}
function uptimeTxt(h) {
  if (h >= 24) { const d = Math.floor(h / 24); return d + 'd ' + (h % 24) + 'h'; }
  return h + 'h';
}
function svcRow(s, dead) {
  const up = s.up && !dead;
  return `<div class="svc${up ? '' : ' down'}"><span class="svc-led"></span><span class="svc-name">${esc(s.name)}</span><span class="svc-port">${esc(s.port)}</span><span class="svc-state">${dead ? 'LOST' : up ? 'UP' : 'DOWN'}</span></div>`;
}
function nodeVitals(n, dead) {
  const v = n.vitals;
  if (dead) return `<div class="vg-list dead">
    ${['CPU TEMP', 'LOAD', 'DISK', 'MEM'].map(l => bar({ label: l, val: '—', pct: 0, tone: 'down' })).join('')}</div>`;
  const tTemp = v.temp >= 78 ? 'crit' : v.temp >= 68 ? 'warn' : 'ok';
  const tLoad = v.load >= n.cores * 0.85 ? 'crit' : v.load >= n.cores * 0.6 ? 'warn' : 'ok';
  const tDisk = v.disk >= 90 ? 'crit' : v.disk >= 80 ? 'warn' : 'ok';
  const tMem = v.mem >= 90 ? 'crit' : v.mem >= 78 ? 'warn' : 'ok';
  return `<div class="vg-list">
    ${bar({ label: 'CPU TEMP', val: round(v.temp, 1) + ' °C', pct: (v.temp - 30) / 55, tone: tTemp })}
    ${bar({ label: 'LOAD', val: round(v.load, 2) + ' / ' + n.cores, pct: v.load / n.cores, tone: tLoad })}
    ${bar({ label: 'DISK', val: round(v.disk) + ' %', pct: v.disk / 100, tone: tDisk })}
    ${bar({ label: 'MEM', val: round(v.mem) + ' %', pct: v.mem / 100, tone: tMem })}
  </div>`;
}
function heartStrip(n, st) {
  const dead = st.key === 'down', stale = st.key === 'stale';
  // 22 recent beats; the most recent at the right pulses live when healthy
  const bars = Array.from({ length: 22 }, (_, i) => {
    const fresh = i >= 19;
    return `<i class="hb${fresh && !dead ? ' live' : ''}${dead ? ' off' : ''}" style="--d:${(i % 6) * 0.08}s"></i>`;
  }).join('');
  const line = dead ? `NO SIGNAL · ${beatTxt(n)}` : stale ? `LATE · ${beatTxt(n)}` : `LAST BEAT ${beatTxt(n)} · EVERY ${n.beatEvery}s`;
  return `<div class="hbwrap ${st.cls}"><div class="hbstrip">${bars}</div><div class="hbline">${line}</div></div>`;
}
function nodeCard(n) {
  const st = nodeStatus(n);
  const dead = st.key === 'down';
  const pill = st.key === 'up' ? 'ok' : st.key === 'degraded' ? 'med' : st.key === 'stale' ? 'med' : 'hi';
  const upCount = dead ? 0 : n.services.filter(s => s.up).length;
  const svcLights = n.services.map(s => `<span class="svc-dot${s.up && !dead ? '' : ' down'}"></span>`).join('');
  const teaserTemp = dead ? 'No heartbeat — node went silent. Last seen ' + beatTxt(n) + '.' : n.role + ' · ' + round(n.vitals.temp) + '°C · ' + round(n.vitals.disk) + '% disk';
  const mcLine = n.players != null && !dead ? `<span>${n.players}/${n.maxPlayers} PLAYERS</span>` : '';

  return `
    <div class="nodewrap ${st.cls}">
    <span class="c-corner tl"></span><span class="c-corner br"></span>
    <div class="c-collapsed">
      <div class="c-top"><span class="c-id">${gIcon('node', 14, 'cmark')}<span class="c-code">${n.code}</span></span><span class="c-pill ${pill}">${st.label}</span></div>
      <div class="c-title sm">${esc(n.name)}</div>
      <div class="node-svclights">${svcLights}<span class="nsl-count">${upCount}/${n.services.length} SVC</span></div>
      <div class="c-teaser node-teaser">${teaserTemp}</div>
      <div class="c-foot"><span class="c-status ${st.key === 'up' ? 'ok' : ''} node-beat ${st.cls}"><span class="hbtick"></span>${beatTxt(n)}</span><span>${esc(n.host.split('·')[0].trim())}</span></div>
    </div>
    <div class="c-full">
      ${fullHead(n.code, n.name, [tag(st.label, pill), tag(n.host, ''), tag(n.ip, '')], 'node')}
      <div class="node-rail">
        <span class="nr-role">${n.role.toUpperCase()}</span>
        ${mcLine}
        <span class="nr-up">UPTIME ${uptimeTxt(n.uptimeH)}</span>
      </div>
      <div class="c-scroll">
        <div class="c-col">
          ${sect(`Services — ${upCount}/${n.services.length} up`, `<div class="svclist">${n.services.map(s => svcRow(s, dead)).join('')}</div>`)}
          ${sect('Heartbeat', heartStrip(n, st))}
        </div>
        <div class="c-col">
          ${sect('Host vitals', nodeVitals(n, dead))}
        </div>
      </div>
    </div>
    </div>`;
}

// ── actions (mutate in-memory + persist to the live API) ───────────────────────
// Writes hit the backend like PROJECTS (a later `sync pull` reconciles the local
// files). The service call is fire-and-forget — the in-scene card already
// re-rendered against the mutated in-memory state; auth/transient failures are
// swallowed (no tokens on the unauth dev route, etc.) so the UI never blocks.
const saveMind = () => {
  VitalsService.putMind({
    schemaVersion: 1, scale: VITALS.mind.scale, labels: VITALS.mind.labels,
    today: VITALS.mind.today, note: VITALS.mind.note, log: VITALS.mind.log,
  }).catch(() => {});
};
const saveCapture = () => { CaptureService.putCapture(CAPTURE.items).catch(() => {}); };

export function setMood(v) { v = +v; if (v >= 1 && v <= 5) { VITALS.mind.today = v; saveMind(); } }
export function setJournal(text) { if (VITALS.mind.today != null) { VITALS.mind.note = String(text || '').trim(); saveMind(); } }
export function addCapture(text) {
  text = String(text || '').trim(); if (!text) return false;
  const id = 'q' + Date.now().toString(36);
  CAPTURE.items.unshift({ id, state: 'inbox', at: new Date().toISOString().slice(0, 16), route: null, text });
  saveCapture(); return true;
}
export function keepCapture(id) { const it = CAPTURE.items.find(i => i.id === id); if (it) { it.state = 'note'; CAPTURE._routing = null; saveCapture(); } }
export function discardCapture(id) { const i = CAPTURE.items.findIndex(x => x.id === id); if (i >= 0) { CAPTURE.items.splice(i, 1); CAPTURE._routing = null; saveCapture(); } }
export function toggleRoute(id) { CAPTURE._routing = CAPTURE._routing === id ? null : id; }   // ephemeral UI state
export function routeCapture(id, pid) { const it = CAPTURE.items.find(i => i.id === id); if (it) { it.state = 'note'; it.route = pid; CAPTURE._routing = null; saveCapture(); } }

// CAPTURE filter is VIEW state (persisted, decoupled from the data — Item 3) so
// it survives a reload AND a live setLiveCapture swap.
export function currentCaptureFilter() {
  const f = getView('capture.filter', 'all');
  return ['all', 'inbox', 'note'].includes(f) ? f : 'all';
}
export function setCaptureFilter(f) { if (['all', 'inbox', 'note'].includes(f)) setView('capture.filter', f); CAPTURE._routing = null; }

// SHELL: read-only homelab summary (status computed like the cards).
export function systemStatus() {
  return SYSTEM.nodes.map(n => {
    const st = nodeStatus(n);
    const up = st.key === 'down' ? 0 : n.services.filter(s => s.up).length;
    return {
      code: n.code, name: n.name, status: st.label,
      up, total: n.services.length,
      temp: n.vitals ? n.vitals.temp : null, sinceBeat: n.sinceBeat,
    };
  });
}
